import { useSyncExternalStore } from "react";

/**
 * How the formatting bar appears, kept per device like the theme, with separate choices for
 * touch screens ("mobile") and mouse-and-keyboard ("desktop"):
 * - always: shown all the time, not only over a selection;
 * - side: a vertical bar at the side of the screen rather than a bubble over the selection.
 * Both off is the bubble menu; both on is a vertical bar always at the side.
 */
export type FormatBarDevice = "mobile" | "desktop";
export type FormatBarOption = "always" | "side";
export type FormatBarSetting = Record<FormatBarOption, boolean>;

export const formatBarDevice: FormatBarDevice = window.matchMedia("(pointer: coarse)").matches ? "mobile" : "desktop";

const key = (device: FormatBarDevice, option: FormatBarOption) => `quill.formatBar.${device}.${option}`;
const listeners = new Set<() => void>();

function read(device: FormatBarDevice): FormatBarSetting {
  try {
    return {
      always: localStorage.getItem(key(device, "always")) === "on",
      side: localStorage.getItem(key(device, "side")) === "on",
    };
  } catch {
    return { always: false, side: false };
  }
}

let settings: Record<FormatBarDevice, FormatBarSetting> = { mobile: read("mobile"), desktop: read("desktop") };

export function setFormatBar(device: FormatBarDevice, option: FormatBarOption, on: boolean) {
  settings = { ...settings, [device]: { ...settings[device], [option]: on } };
  try {
    if (on) localStorage.setItem(key(device, option), "on");
    else localStorage.removeItem(key(device, option));
  } catch {
    // ignore
  }
  listeners.forEach((l) => l());
}

function subscribe(fn: () => void) {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/** Both devices' choices (for Settings). */
export function useFormatBarSettings(): Record<FormatBarDevice, FormatBarSetting> {
  return useSyncExternalStore(subscribe, () => settings);
}

/** This device's choice (for the editor). */
export function useFormatBar(): FormatBarSetting {
  return useSyncExternalStore(subscribe, () => settings[formatBarDevice]);
}
